import { Component, EventEmitter, Input, Output } from '@angular/core';

import { EnvService } from '@lib/env';
import { UpdateContactPersonsRequest, TransmissionSupplierBasket } from '../model/basket.model';
import {
    Basket,
    BasketAddress,
    SupplierBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';

@Component({
    selector: 'basket-addresses',
    templateUrl: './basket-addresses.component.html',
    styleUrls: ['./basket-addresses.component.scss'],
    standalone: false,
})
export class BasketAddressesComponent {
    @Input() deliveryAddress: BasketAddress | undefined;
    @Input() invoiceAddress: BasketAddress | undefined;
    @Input() basket: Basket | undefined;
    @Input() isMoreThanOneInvoiceAddress = false;
    @Input() showStepperButtons = false;
    @Input() isEditableStep!: boolean;
    @Input() supplierBaskets: SupplierBasket[] | undefined;

    @Output() nextClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() validityChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() previousClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() deliveryAddressDialogOpened: EventEmitter<void> = new EventEmitter<void>();
    @Output() invoiceAddressDialogOpened: EventEmitter<void> = new EventEmitter<void>();
    @Output() transmissionInfoUpdated = new EventEmitter<TransmissionSupplierBasket>();
    @Output() contactPersonsUpdated = new EventEmitter<UpdateContactPersonsRequest>();

    env = EnvService;

    onClickNext(): void {
        if (!this.canProceedToSummary()) {
            this.validityChanged.next(false);
            return;
        }
        this.validityChanged.next(true);
        this.nextClicked.emit();
    }

    onClickPrevious(): void {
        this.previousClicked.emit();
    }

    openDeliveryAddressDialog() {
        this.deliveryAddressDialogOpened.emit();
    }

    openInvoiceAddressDialog() {
        this.invoiceAddressDialogOpened.emit();
    }

    updateTransmissionInfo(transmissionSupplierBasket: TransmissionSupplierBasket): void {
        this.transmissionInfoUpdated.emit(transmissionSupplierBasket);
    }

    updateValidity(): void {
        this.validityChanged.next(this.canProceedToSummary());
    }

    updateContactPersonsWithMapping(updateContactPersonsRequest: UpdateContactPersonsRequest) {
        this.contactPersonsUpdated.emit(updateContactPersonsRequest);
    }

    /**
     * UC_BA_1021: a supplier contact is mandatory only when the tenant setting "Ansprechpartner
     * vorbelegen" is off ({@link Basket.contactSelectionRequired}). In that case every supplier
     * basket that offers selectable contacts must have at least one chosen. The exactly-one case is
     * auto-selected by BasketAddressItemComponent, so it never blocks here.
     *
     * Threshold is ">= 1" (not the old "> 1"): with preselection off a single available contact is
     * still mandatory (and auto-selected).
     *
     * Fail closed (EPROC-11496 reopen): while the dropdown is not loaded yet (undefined) the gate
     * blocks until the contacts arrive — basket-applying store paths (e.g. a company-code change)
     * replace the supplier baskets and drop the FE-only dropdown, and the old fail-open check let
     * the checkout pass into the server-side 422. A loaded but empty dropdown ([]) keeps the
     * supplier orderable (no deadlock, UC_BA_1021). The server-side rule
     * (SupplierBasketContactRequiredRule) remains the hard guard.
     */
    isSupplierContactSelectionMissing(): boolean {
        if (!this.basket?.contactSelectionRequired) {
            return false;
        }
        if (!this.supplierBaskets) {
            return true; // selection is required but the baskets are not visible yet -> fail closed
        }
        return this.supplierBaskets.some((supplierBasket) => {
            const dropdown = supplierBasket?.supplierContactsDropdown;
            if (!dropdown) {
                return true; // not loaded yet -> block until we know (fail closed)
            }
            return dropdown.length >= 1 && (supplierBasket?.supplierContacts?.length ?? 0) === 0;
        });
    }

    hasInvoiceAddress(): boolean {
        return !!this.invoiceAddress;
    }

    canProceedToSummary(): boolean {
        return this.hasInvoiceAddress() && !this.isSupplierContactSelectionMissing();
    }
}
